import React, { createRef, useCallback, useState } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import Carousel from 'react-multi-carousel';
import Image from 'next/image';

import Button from 'src/components/Button';
import FontIcon from 'src/components/FontIcon';
import FormatSchedule from 'src/components/FormatSchedule';

import theme from 'src/styles/components/RestaurantDetails.module.scss';
import FooterLogo from 'src/components/FooterLogo';

const RestaurantDetails = ({
    className,
    name,
    description,
    address,
    logoUrl,
    logoSrcSetUrls,
    gallery,
    facebookUrl,
    instagramUrl,
    siteUrl,
    schedule,
    isOpenTimeDefined
}) => {
    const [currentSlide, setCurrentSlide] = useState(0);
    const carouselRef = createRef();

    const responsive = {
        mobile: {
            breakpoint: { max: 99999, min: 0 },
            items: 1,
            partialVisibilityGutter: 30 // this is needed to tell the amount of px that should be visible.
        }
    };

    const handleChange = useCallback((slideIndex) => {
        carouselRef.current.goToSlide(slideIndex, true);
        setCurrentSlide(slideIndex);
    });

    return (
        <div className={classnames(className, theme.container)}>
            <div className={theme.gallery}>
                <Carousel
                  className={theme.carousel}
                  responsive={responsive}
                  arrows={false}
                  ref={carouselRef}
                >
                    {gallery.map(({ image, imageSrcSet, thumbnail }) => (
                        <div className={theme.galleryItem} key={thumbnail}>
                            {image && (
                                <Image
                                  src={image}
                                  srcset={imageSrcSet}
                                  alt="bg"
                                  width={60}
                                  height={60}
                                  unoptimized="true"
                                />
                            )}
                        </div>
                    ))}
                </Carousel>
            </div>
            <div className={classnames(theme.column, { [theme.active]: currentSlide === 0 })}>
                <div className={theme.content}>
                    <div className={theme.socialMedia}>
                        {instagramUrl && (
                            <Button
                              className={theme.socialButton}
                              floating
                              mini
                              icon="icon-instagram"
                              href={instagramUrl}
                              blankTarget
                            />
                        )}
                        {facebookUrl && (
                            <Button
                              className={theme.socialButton}
                              floating
                              mini
                              icon="icon-facebook"
                              href={facebookUrl}
                              blankTarget
                            />
                        )}
                        {siteUrl && (
                            <Button
                              className={theme.socialButton}
                              floating
                              mini
                              icon="icon-website"
                              href={siteUrl}
                              blankTarget
                            />
                        )}

                    </div>
                    <p className={theme.description}>{description}</p>
                    <div className={theme.flexibleContent}>
                        <div className={theme.addressContainer}>
                            <FontIcon className={theme.icon} value="icon-pin" />
                            <p className={theme.address}>{address}</p>
                        </div>
                        {isOpenTimeDefined && (
                            <div className={theme.hoursContainer}>
                                <FontIcon className={theme.icon} value="icon-clock" />
                                <div className={theme.hours}>
                                    {schedule && (
                                        <FormatSchedule
                                          className={theme.schedule}
                                          schedule={schedule}
                                        />
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <div className={theme.bar}>
                <div className={theme.logoContainer}>
                    {(logoUrl && (
                        <div className={theme.logo}>
                            <Image
                              src={logoUrl}
                              alt={name}
                              width={100}
                              height={100}
                              srcset={logoSrcSetUrls}
                              unoptimized="true"
                            />
                        </div>
                    ))}
                    <div className={theme.applicationLogo}>
                        <FooterLogo />
                    </div>
                </div>
                <div className={theme.galleryBar}>
                    {gallery.map(({ thumbnail, thumbnailSrcSet }, index) => (
                        <div
                          key={thumbnail}
                          className={classnames(theme.card, { [theme.active]: currentSlide === index })}
                          onClick={() => { handleChange(index); }}
                          role="button"
                          tabIndex={0}
                        >
                            {thumbnail && (
                                <Image
                                  src={thumbnail}
                                  srcset={thumbnailSrcSet}
                                  width={60}
                                  height={60}
                                  unoptimized="true"
                                  alt=""
                                />
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

RestaurantDetails.propTypes = {
    className: PropTypes.string,
    description: PropTypes.string,
    name: PropTypes.string,
    address: PropTypes.string,
    logoUrl: PropTypes.string.isRequired,
    logoSrcSetUrls: PropTypes.string.isRequired,
    facebookUrl: PropTypes.string,
    instagramUrl: PropTypes.string,
    siteUrl: PropTypes.string,
    gallery: PropTypes.arrayOf(PropTypes.shape({
        image: PropTypes.string,
        thumbnail: PropTypes.string.isRequired
    })).isRequired,
    schedule: PropTypes.object,
    isOpenTimeDefined: PropTypes.bool
};

export default RestaurantDetails;
